import type React from 'react';
import styled from '@emotion/styled';
import tw from 'twin.macro';
import { type VariantProps } from 'cva';
import { omit } from 'lodash';
import { cs } from '@bbkAdminUtils/css-utils';
import { compose, cva } from '@bbkShared/utils/css-utils';

export type ButtonBase = React.DetailedHTMLProps<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  HTMLButtonElement
> & {
  primary?: boolean;
  secondary?: boolean;
  success?: boolean;
  danger?: boolean;
  warning?: boolean;
  tab?: boolean;
  tabVertical?: boolean;
  active?: boolean;
  radio?: boolean;
  transparent?: boolean;
  link?: boolean;
  circle?: boolean;
  small?: boolean;
  centerAll?: boolean;
};

export const CircleIcon: React.FC<
  React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>
> = ({ className, ...props }) => {
  return (
    <div
      className={cs(
        'tw-flex tw-items-center tw-justify-center',
        'tw-h-8 tw-w-8',
        'tw-rounded-full tw-shadow',
        'tw-bg-cbrprimary-50 tw-text-cbrprimary-800',
        className
      )}
      {...props}
    />
  );
};

const commonButtonV2Variants = cva({
  base: [
    'tw-font-bold tw-text-sm tw-leading-4',
    '[&_svg]:tw-w-[18px]',
    'hover:tw-shadow focus:tw-shadow',
    'focus:tw-outline-none',
    'active:tw-scale-95',
    'disabled:tw-opacity-40 disabled:tw-cursor-not-allowed',
  ],
});

const colorButtonV2Variants = cva({
  variants: {
    color: {
      default: [
        'tw-text-cbrneutral-500 tw-bg-white',
        'hover:tw-text-cbrprimary-800 hover:tw-bg-cbrprimary-50',
        'focus:tw-text-cbrprimary-800 focus:tw-bg-cbrprimary-50',
      ],
      secondary: [
        'tw-text-cbrprimary-600 tw-bg-cbrprimary-50',
        'hover:tw-text-cbrprimary-800',
        'focus:tw-text-cbrprimary-800',
        'active:tw-bg-cbrprimary-200',
      ],
      primary: [
        'tw-text-white tw-bg-cbrprimary-800',
        'hover:tw-bg-cbrprimary-700',
        'focus:tw-bg-cbrprimary-700',
      ],
      success: [
        'tw-text-white tw-bg-cbrsuccess-500',
        'hover:tw-bg-cbrsuccess-400',
        'focus:tw-bg-cbrsuccess-400',
      ],
      danger: [
        'tw-text-white tw-bg-cbrdanger-500',
        'hover:tw-bg-cbrdanger-400',
        'focus:tw-bg-cbrdanger-400',
      ],
      warning: [
        'tw-text-white tw-bg-cbryellow-400',
        'hover:tw-bg-cbryellow-500',
        'focus:tw-bg-cbryellow-500',
      ],
      link: [
        'tw-text-cbrprimary-800',
        'hover:tw-text-cbrprimary-600',
        'focus:tw-text-cbrprimary-600',
        'hover:tw-shadow-none focus:tw-shadow-none',
      ],
      transparent: [
        'tw-text-cbrneutral-500',
        'hover:tw-text-cbrprimary-600',
        'focus:tw-text-cbrprimary-600',
        'hover:tw-shadow-none focus:tw-shadow-none',
      ],
    },
  },
  defaultVariants: {
    color: 'default',
  },
});

const buttonV2Variants = compose(
  commonButtonV2Variants,
  colorButtonV2Variants,
  cva({
    base: ['tw-rounded'],
    variants: {
      size: {
        default: 'tw-p-[9px_15px_10px_15px]',
        small: 'tw-p-[5px]',
      },
      centerAll: {
        true: 'tw-flex tw-items-center [&>*]:tw-flex',
        false: '',
      },
    },
    defaultVariants: {
      size: 'default',
      centerAll: false,
    },
  })
);

const circleButtonV2Variants = compose(
  commonButtonV2Variants,
  colorButtonV2Variants,
  cva({
    base: 'tw-p-0 tw-rounded-full',
    variants: {
      size: {
        default: 'tw-h-9 tw-w-9',
        small: 'tw-h-6 tw-w-6',
      },
    },
    defaultVariants: {
      size: 'default',
    },
  })
);

const radioButtonV2Variants = compose(
  commonButtonV2Variants,
  cva({
    base: [
      'tw-p-[8px_15px_9px_15px]',
      'tw-rounded',
      'tw-border tw-border-solid',
    ],
    variants: {
      active: {
        false: [
          'tw-text-cbrneutral-500 tw-bg-white',
          'tw-border-cbrneutral-400',
          'hover:tw-text-cbrprimary-800 hover:tw-bg-cbrprimary-50',
          'focus:tw-text-cbrprimary-800 focus:tw-bg-cbrprimary-50',
          'active:tw-text-white active:tw-bg-cbrprimary-600',
        ],
        true: [
          'tw-text-white tw-bg-cbrprimary-800',
          'tw-border-cbrprimary-800',
          'active:tw-scale-100',
        ],
      },
    },
    defaultVariants: {
      active: false,
    },
  })
);
const tabButtonV2Variants = compose(
  commonButtonV2Variants,
  cva({
    base: [
      'tw-p-[9px_15px_10px_15px]',
      'tw-rounded',
      'tw-shadow-none',
      'active:tw-scale-100',
      'active:tw-shadow-none',
      'tw-border-solid tw-border-0',
      'hover:tw-shadow-none focus:tw-shadow-none',
      'hover:tw-text-cbrprimary-800 hover:tw-bg-cbrprimary-50',
      'focus:tw-text-cbrprimary-800 focus:tw-bg-cbrprimary-50',
      'active:tw-border-cbrprimary-800',
      'active:tw-rounded-none active:tw-bg-white',
    ],
    variants: {
      active: {
        false: [' tw-bg-white tw-text-cbrneutral-500', 'tw-border-transparent'],
        true: [
          'tw-text-cbrprimary-800 tw-bg-white',
          'tw-border-cbrprimary-800 ',
          'tw-rounded-none',
          'hover:tw-bg-white focus:tw-bg-white',
        ],
      },
      orientation: {
        horizontal: 'tw-border-b-[3px] active:tw-border-b-[3px]',
        vertical: 'tw-border-l-[3px] active:tw-border-l-[3px]',
      },
    },
    defaultVariants: {
      active: false,
      orientation: 'horizontal',
    },
  })
);

type BtnV21Props =
  | ({ shape?: 'button' } & VariantProps<typeof buttonV2Variants>)
  | ({ shape: 'circle' } & VariantProps<typeof circleButtonV2Variants>)
  | ({ shape: 'radio' } & VariantProps<typeof radioButtonV2Variants>)
  | ({ shape: 'tab' } & VariantProps<typeof tabButtonV2Variants>);

export const ButtonV21: React.FC<
  React.DetailedHTMLProps<
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    HTMLButtonElement
  > &
    BtnV21Props
> = ({ className, ...props }) => {
  let classes;
  switch (props.shape) {
    case 'circle': {
      const { size, color } = props;
      classes = circleButtonV2Variants({
        size,
        color: color ?? 'secondary',
        className,
      });
      break;
    }
    case 'radio': {
      const { active } = props;
      classes = radioButtonV2Variants({
        active,
        className,
      });
      break;
    }
    case 'tab': {
      const { active, orientation } = props;
      classes = tabButtonV2Variants({
        active,
        orientation,
        className,
      });
      break;
    }
    default: {
      const { size, color } = props;
      classes = buttonV2Variants({ size, color, className });
      break;
    }
  }

  return (
    <button
      className={cs(classes)}
      {...omit(props, ['shape', 'color', 'centerAll', 'active', 'orientation'])}
    />
  );
};

export const ButtonV22: React.FC<
  React.DetailedHTMLProps<
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    HTMLButtonElement
  > &
    ButtonBase
> = ({
  centerAll,
  circle,
  radio,
  tab,
  tabVertical,
  active,
  small,
  primary,
  secondary,
  transparent,
  link,
  success,
  danger,
  warning,
  color: propsColor,
  ...props
}) => {
  let btn21Props: BtnV21Props;

  let color: VariantProps<typeof colorButtonV2Variants>['color'];
  switch (true) {
    case primary:
      color = 'primary';
      break;
    case secondary:
      color = 'secondary';
      break;
    case transparent:
      color = 'transparent';
      break;
    case link:
      color = 'link';
      break;
    case success:
      color = 'success';
      break;
    case danger:
      color = 'danger';
      break;
    case warning:
      color = 'warning';
      break;
    default:
      color = 'default';
      break;
  }

  switch (true) {
    case circle:
      btn21Props = { shape: 'circle', color };
      break;
    case radio:
      btn21Props = { shape: 'radio', active };
      break;
    case tab:
    case tabVertical:
      btn21Props = {
        shape: 'tab',
        orientation: tab ? 'horizontal' : 'vertical',
        active,
      };
      break;
    default:
      btn21Props = {
        shape: 'button',
        size: small ? 'small' : 'default',
        centerAll,
        color,
      };
  }

  return <ButtonV21 {...btn21Props} {...props} />;
};

export const ButtonV2 = styled.button<ButtonBase>`
  ${tw`tw-font-bold tw-text-sm tw-leading-4`}
  ${(props: ButtonBase) => {
    if (props.centerAll) {
      return tw`tw-flex tw-items-center [&>*]:tw-flex`;
    }
  }}
  ${(props: ButtonBase) => {
    if (props.circle && props.small) return tw`tw-h-6 tw-w-6`;
    if (props.circle) return tw`tw-h-9 tw-w-9`;
  }}
  ${tw`[&_svg]:tw-w-[18px]`}
  ${(props: ButtonBase) => {
    if (props.radio) return tw`tw-p-[8px_15px_9px_15px]`;
    if (props.circle) return tw`tw-p-0`;
    if (props.small) return tw`tw-p-[5px]`;
    return tw`tw-p-[9px_15px_10px_15px]`;
  }}
  ${(props: ButtonBase) => {
    if (props.circle) return tw`tw-rounded-full`;
    if (props.tab && props.active) return tw`tw-rounded-none`;
    if (props.tabVertical && props.active) return tw`tw-rounded-none`;
    return tw`tw-rounded`;
  }}
  ${(props: ButtonBase) => {
    if (props.tab && props.active) return tw`tw-text-cbrprimary-800`;
    if (props.tabVertical && props.active) return tw`tw-text-cbrprimary-800`;
    if (props.radio && props.active) return tw`tw-text-white`;
    if (props.success) return tw`tw-text-white`;
    if (props.danger) return tw`tw-text-white`;
    if (props.warning) return tw`tw-text-white`;
    if (props.secondary) return tw`tw-text-cbrprimary-600`;
    if (props.circle) return tw`tw-text-cbrprimary-600`;
    if (props.link) return tw`tw-text-cbrprimary-800`;
    if (props.primary) return tw`tw-text-white`;
    return tw`tw-text-cbrneutral-500`;
  }}
  ${(props: ButtonBase) => {
    if (props.tab && props.active) return tw`tw-bg-white`;
    if (props.tabVertical && props.active) tw`tw-bg-white`;
    if (props.radio && props.active) return tw`tw-bg-cbrprimary-800`;
    if (props.primary) return tw`tw-bg-cbrprimary-800`;
    if (props.secondary) return tw`tw-bg-cbrprimary-50`;
    if (props.circle) return tw`tw-bg-cbrprimary-50`;
    if (props.link) return tw`tw-bg-transparent`;
    if (props.transparent) return tw`tw-bg-transparent`;
    if (props.success) return tw`tw-bg-cbrsuccess-500`;
    if (props.danger) return tw`tw-bg-cbrdanger-500`;
    if (props.warning) return tw`tw-bg-cbryellow-400`;
    return tw`tw-bg-white`;
  }}
  ${(props: ButtonBase) => {
    if (props.tab && props.active)
      return tw`tw-border tw-border-solid tw-border-0 tw-border-cbrprimary-800 tw-border-b-[3px]`;
    if (props.tab)
      return tw`tw-border tw-border-solid tw-border-0 tw-border-transparent tw-border-b-[3px]`;
    return tw`tw-border-b-0`;
  }}
  ${(props: ButtonBase) => {
    if (props.tabVertical && props.active)
      return tw`tw-border tw-border-solid tw-border-0 tw-border-cbrprimary-800 tw-border-l-[3px]`;
    if (props.tabVertical)
      return tw`tw-border tw-border-solid tw-border-0 tw-border-white tw-border-l-[3px]`;
    return tw`tw-border-l-0`;
  }}
  ${(props: ButtonBase) => {
    if (props.radio && props.active)
      return tw`tw-border tw-border-solid tw-border-cbrprimary-800`;
    if (props.radio)
      return tw`tw-border tw-border-solid tw-border-cbrneutral-400`;
  }}
  ${tw`focus:tw-outline-none`}
  &:hover,
  &:focus {
    ${(props: ButtonBase) => {
      if (props.primary) return tw`tw-bg-cbrprimary-700`;
      if (props.secondary) return tw`tw-bg-cbrprimary-50`;
      if (props.circle) return tw`tw-bg-cbrprimary-50`;
      if (props.success) return tw`tw-bg-cbrsuccess-400`;
      if (props.danger) return tw`tw-bg-cbrdanger-400`;
      if (props.warning) return tw`tw-bg-cbryellow-500`;
      if (props.link) return tw`tw-bg-transparent`;
      if (props.tab && props.active) return tw`tw-bg-transparent`;
      if (props.tabVertical && props.active) return tw`tw-bg-transparent`;
      if (props.radio && props.active) return tw`tw-bg-cbrprimary-800`;
      if (props.transparent) return tw`tw-bg-transparent`;
      return tw`tw-bg-cbrprimary-50`;
    }}
    ${(props: ButtonBase) => {
      if (props.primary) return tw`tw-text-white`;
      if (props.success) return tw`tw-text-white`;
      if (props.danger) return tw`tw-text-white`;
      if (props.warning) return tw`tw-text-white`;
      if (props.link) return tw`tw-text-cbrprimary-600`;
      if (props.radio && props.active) return tw`tw-text-white`;
      if (props.transparent) return tw`tw-text-cbrprimary-600`;
      return tw`tw-text-cbrprimary-800`;
    }}
    ${(props: ButtonBase) => {
      if (props.transparent || props.link) {
        return;
      }
      if (
        props.primary ||
        props.success ||
        props.danger ||
        props.warning ||
        props.secondary ||
        props.circle ||
        props.small ||
        props.radio
      ) {
        return tw`tw-shadow`;
      }
    }}
  }

  &:active {
    ${(props: ButtonBase) => {
      if (props.tab) return tw`tw-bg-white`;
      if (props.primary) return tw`tw-bg-cbrprimary-600`;
      if (props.secondary) return tw`tw-bg-cbrprimary-200`;
      if (props.circle) return tw`tw-bg-cbrprimary-200`;
      if (props.success) return tw`tw-bg-cbrsuccess-400`;
      if (props.danger) return tw`tw-bg-cbrdanger-400`;
      if (props.warning) return tw`tw-bg-cbryellow-500`;
      if (props.link) return tw`tw-bg-transparent`;
      if (props.radio && props.active) return tw`tw-bg-cbrprimary-800`;
      if (props.radio) return tw`tw-bg-cbrprimary-600`;
      if (props.transparent) return tw`tw-bg-transparent`;
      return tw`tw-bg-cbrprimary-50`;
    }}
    ${(props: ButtonBase) => {
      if (props.tab) return;
      if (props.active) return;
      return tw`tw-scale-95`;
    }}
    ${(props: ButtonBase) => {
      if (props.tab)
        return tw`tw-border tw-border-solid tw-border-0 tw-border-cbrprimary-800 tw-border-b-[3px]`;
    }}
    ${(props: ButtonBase) => {
      if (props.tab) return tw`tw-rounded-none`;
    }}
    ${(props: ButtonBase) => {
      if (props.radio) return tw`tw-text-white`;
    }}
  }

  &:disabled {
    ${tw`
      tw-opacity-40
      tw-cursor-not-allowed`}
  }
`;
